import React from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Edit, Eye, Star, Trash2 } from 'lucide-react'
import type { Service } from '@/features/services/types'
import { getServiceStatusBadgeClass } from '@/features/services/utils'

interface ServicesTableProps {
  services: Service[]
  onView: (service: Service) => void
  onEdit: (service: Service) => void
  onDelete: (id: string) => void
  onStatusChange: (id: string, status: Service['status']) => void
  deletePending: boolean
}

export function ServicesTable({
  services,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  deletePending,
}: ServicesTableProps) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead>Footer Service</TableHead>
              <TableHead>Reviews</TableHead>
              <TableHead>Gallery Images</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="w-[50px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.map((service) => (
              <TableRow key={service.id}>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{service.title}</div>
                    <div className="text-sm text-muted-foreground line-clamp-1">{service.description}</div>
                    <div className="text-xs text-muted-foreground">/{service.slug}</div>
                    {service.hero_headline && (
                      <div className="text-xs text-blue-600 font-medium">{service.hero_headline}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <select
                    value={service.status}
                    onChange={(event) => onStatusChange(service.id, event.target.value as Service['status'])}
                    className={`px-2 py-1 rounded-md text-xs font-medium ${getServiceStatusBadgeClass(
                      service.status
                    )}`}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </TableCell>
                <TableCell>
                  {service.is_featured ? (
                    <Star className="h-4 w-4 text-yellow-500 fill-current" />
                  ) : (
                    <Star className="h-4 w-4 text-muted-foreground" />
                  )}
                </TableCell>
                <TableCell>
                  {service.is_footer_service ? (
                    <Badge variant="secondary" className="text-xs">
                      Footer
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm">{service.review_ids ? service.review_ids.length : 0} reviews</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{service.gallery_images ? service.gallery_images.length : 0} images</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-muted-foreground">
                    {new Date(service.updated_at).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onView(service)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(service)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => onDelete(service.id)}
                      disabled={deletePending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
